import { OrgUserSearchParams } from '@cibo/core'
import ClearIcon from '@mui/icons-material/Clear'
import Search from '@mui/icons-material/Search'
import { IconButton, Stack, TextField, useMediaQuery, useTheme } from '@mui/material'
import debounce from 'lodash/debounce'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { UserSearchFilters, UserSearchFiltersProps } from './UserSearchFilters'

interface UserSearchBarProps {
  loading: boolean
  onSearch(searchValues: OrgUserSearchParams): void
  searchValues: OrgUserSearchParams
}

export const UserSearchBar = ({
  loading,
  onSearch,
  searchValues,
  ...userSearchFiltersProps
}: UserSearchBarProps & Pick<UserSearchFiltersProps, 'availableFilters'>) => {
  const { t } = useTranslation('@cibo/profile/PaginatedUsersTable')
  const theme = useTheme()
  const isMdDown = useMediaQuery(theme.breakpoints.down('md'))
  const [inputText, setInputText] = useState<string>(searchValues.fullText || '')

  const debouncedSearch = useCallback(
    debounce(query => onSearch(query), 200),
    []
  )

  const onChange = async ({ target }: any) => {
    const fullText = target.value?.trim().length === 0 ? undefined : target.value

    if (!!fullText) {
      setInputText(fullText)
      debouncedSearch({
        ...searchValues,
        fullText,
      })
    } else {
      setInputText('')
      debouncedSearch({
        ...searchValues,
        fullText: '',
      })
    }
  }

  const handleClearClick = () => {
    setInputText('')
    onSearch({
      ...searchValues,
      fullText: '',
    })
  }

  const handleUpdateFilters = (values: OrgUserSearchParams, replace?: boolean) => {
    return !!replace
      ? onSearch(values)
      : onSearch({
          ...searchValues,
          ...values,
        })
  }
  return (
    <Stack
      direction={isMdDown ? 'column' : 'row'}
      alignItems="center"
      justifyContent="flex-end"
      spacing={2}
      flex={1}
    >
      <TextField
        data-testid="user-search-input"
        label={t('searchGrower')}
        InputProps={{
          endAdornment: (
            <>
              <IconButton
                sx={{ visibility: inputText ? 'visible' : 'hidden' }}
                onClick={handleClearClick}
                size="small"
              >
                <ClearIcon />
              </IconButton>
              <Search color="primary" />
            </>
          ),
          // @ts-ignore not providing this causes a browser warning, but MUI doesn't like it.
          disableunderline: 'true',
        }}
        onKeyDown={ev => {
          if (ev.key === 'Enter') {
            onSearch({
              ...searchValues,
              fullText: inputText,
            })
            ev.preventDefault()
          }
        }}
        value={inputText}
        onChange={onChange}
      />
      <UserSearchFilters
        filterValues={searchValues}
        onUpdate={handleUpdateFilters}
        {...userSearchFiltersProps}
      />
    </Stack>
  )
}
